import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class AwpProductForm extends Component {
  #productData = null;
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    const dataScript = this.querySelector('[ref="productData"]');
    if (dataScript) {
      try {
        this.#productData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('AWP Product Form: Failed to parse product data');
      }
    }

    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleThemeVariantUpdate);

    this.#initPanelState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleThemeVariantUpdate);
  }

  /** @param {Event} event */
  #handleThemeVariantUpdate = (event) => {
    // When the default theme's variant-picker changes, sync the variant to our form and button
    const customEvent = /** @type {CustomEvent} */ (event);
    const variant = customEvent?.detail?.resource;
    if (variant) {
      this.#applyVariant(variant);
    }
  };

  #initPanelState() {
    let checkedRadio = this.querySelector('input[name="awp_purchase_type"]:checked');
    if (!checkedRadio) {
      checkedRadio = this.querySelector('input[name="awp_purchase_type"]');
      if (checkedRadio) checkedRadio.checked = true;
    }
    if (checkedRadio) {
      this.#expandPanelFor(checkedRadio.value);
      if (checkedRadio.value === 'frequency') {
        this.#updateSellingPlan('frequency');
      }
    }
    this.#syncButtonText();
  }

  #currentAddText() {
    const btn = this.refs.addToCartBtn;
    if (!btn) return 'Add To Cart';
    const checked = this.querySelector('input[name="awp_purchase_type"]:checked');
    const isSubscription = checked ? checked.value !== 'onetime' : false;
    if (isSubscription && btn.dataset.subscribeText) return btn.dataset.subscribeText;
    return btn.dataset.addText || 'Add To Cart';
  }

  #syncButtonText() {
    const btn = this.refs.addToCartBtn;
    if (!btn || btn.classList.contains('awp_add-to-cart__btn--loading')) return;
    // Sold-out variants render as a "Notify me" trigger with no `disabled`
    // attribute (it needs to stay clickable to open the notify dialog), so
    // don't stomp its label with the regular add-to-cart text.
    if (btn.hasAttribute('data-notify-trigger') || btn.disabled) return;
    btn.textContent = this.#currentAddText();
  }

  handleGrindChange() {
    if (!this.refs.grindSelect || !this.refs.variantInput || !this.#productData) return;

    const selectedGrind = this.refs.grindSelect.value;
    const grindValues = Array.from(this.refs.grindSelect.options).map((o) => o.value);
    const currentVariant = this.#productData.variants.find(
      (v) => String(v.id) === this.refs.variantInput.value
    );
    const variant = this.#findVariant(selectedGrind, currentVariant, grindValues);

    if (variant) this.#applyVariant(variant);
  }

  /**
   * @param {{value?: string}} data
   * @param {Event} [event]
   */
  handleSizeChange(data, event) {
    if (!this.refs.variantInput || !this.#productData) return;

    if (data?.value == null) return;
    // Component.js's on:click data parsing coerces numeric-looking query values
    // (e.g. a size value of "500") to a JS number — force back to string so
    // comparisons against dataset.value / variant.options (always strings) hold.
    const selectedSize = String(data.value);

    // Quick Add moves the size-selector markup out of this component's DOM
    // subtree (see awp-quick-add.js #placeSizeSelector), so its buttons never
    // resolve as refs there — walk from the clicked button itself instead,
    // which works whether it's still nested in the form (PDP) or not (modal).
    const clickedBtn = /** @type {HTMLElement | undefined} */ (event?.target)?.closest?.('.awp_size-selector__btn');
    const buttonsContainer = clickedBtn?.closest('.awp_size-selector__buttons');
    const sizeButtons = buttonsContainer
      ? Array.from(buttonsContainer.querySelectorAll('.awp_size-selector__btn'))
      : [];
    const sizeValues = sizeButtons.map((btn) => btn.dataset.value);
    const currentVariant = this.#productData.variants.find(
      (v) => String(v.id) === this.refs.variantInput.value
    );
    const variant = this.#findVariant(selectedSize, currentVariant, sizeValues);

    if (variant) {
      sizeButtons.forEach((btn) => {
        btn.classList.toggle('awp_size-selector__btn--active', btn.dataset.value === selectedSize);
      });
      this.#applyVariant(variant);

      // Size (unlike grind) can change price and featured media, which this
      // component doesn't render itself. Announce the change on `document` —
      // assets/awp_variant_price_sync.js does the actual work: price via a
      // lightweight Section Rendering API fetch, media instantly via the
      // gallery's own slideshow (variant.featured_media is already in
      // #productData, no fetch needed for that part).
      document.dispatchEvent(
        new CustomEvent('awp:variant-change', {
          detail: {
            productId: this.dataset.productId,
            variantId: variant.id,
            available: variant.available,
            featuredMediaId: variant.featured_media ? variant.featured_media.id : null,
          },
        })
      );
    }
  }

  /** @param {{id: number, available: boolean}} variant */
  #applyVariant(variant) {
    if (!this.refs.variantInput) return;
    this.refs.variantInput.value = variant.id;

    const btn = this.refs.addToCartBtn;
    if (!btn) return;

    if (!variant.available) {
      // Sold-out variants can render as a "Notify me" trigger (see
      // #syncButtonText) that must stay clickable to open the notify dialog —
      // don't disable it or stomp its label with "Sold Out".
      if (btn.hasAttribute('data-notify-trigger')) return;
      btn.disabled = true;
      btn.textContent = btn.dataset.soldText || 'Sold Out';
    } else {
      btn.disabled = false;
      btn.textContent = this.#currentAddText();
    }
  }

  handleQuantityDecrease() {
    if (!this.refs.quantityInput) return;
    const current = parseInt(this.refs.quantityInput.value) || 1;
    if (current > 1) {
      this.refs.quantityInput.value = current - 1;
    }
  }

  handleQuantityIncrease() {
    if (!this.refs.quantityInput) return;
    const current = parseInt(this.refs.quantityInput.value) || 1;
    this.refs.quantityInput.value = current + 1;
  }

  handleQuantityInput() {
    if (!this.refs.quantityInput) return;
    let val = parseInt(this.refs.quantityInput.value) || 1;
    if (val < 1) val = 1;
    this.refs.quantityInput.value = val;
  }

  handlePurchaseTypeChange(event) {
    const value = event.target.value;
    this.#collapseAllPanels();
    this.#expandPanelFor(value);
    this.#updateSellingPlan(value);
    this.#syncButtonText();
  }

  handleFrequencyChange() {
    if (!this.refs.frequencySelect || !this.refs.sellingPlanInput) return;
    this.refs.sellingPlanInput.value = this.refs.frequencySelect.value;
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const btn = this.refs.addToCartBtn;
    if (btn) {
      btn.disabled = true;
      btn.classList.add('awp_add-to-cart__btn--loading');
      btn.textContent = 'Adding...';
    }

    const formData = new FormData(form);

    if (!formData.get('selling_plan')) {
      formData.delete('selling_plan');
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        const didError = !!response.status;

        if (didError) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors
            )
          );
          this.#resetButton(false);
          if (this.refs.liveRegion) {
            this.refs.liveRegion.textContent = response.description || 'Error adding to cart';
          }
        } else {
          if (this.refs.liveRegion) {
            this.refs.liveRegion.textContent = 'Item added to cart';
          }
          this.#resetButton(true);
        }

        // Open the drawer / morph the cart section straight from the add
        // response. A prior refactor (commit 0431c79) gated this dispatch behind
        // a second fetch('/cart.js') just to read item_count — that extra
        // round-trip made the drawer pop out *after* the button had already
        // finished un-loading (the button reset runs on its own 1000ms timer).
        // item_count isn't needed here: response.sections (requested above)
        // already carries the refreshed cart-bubble count via the morph.
        this.dispatchEvent(
          new CartAddEvent({}, this.id || '', {
            didError,
            source: 'awp-product-form',
            itemCount: Number(formData.get('quantity')) || 1,
            productId: this.dataset.productId,
            sections: didError ? undefined : response.sections,
          })
        );
      })
      .catch((error) => {
        console.error('AWP Product Form: Cart add error', error);
        this.#resetButton(false);
      });
  }

  /**
   * @param {string} value - The option value being selected (e.g. a grind or size).
   * @param {{options?: string[]}} [currentVariant]
   * @param {string[]} [siblingValues] - Every possible value for the same option
   *   dimension as `value` (e.g. all grind or all size values), used to locate
   *   which slot in `currentVariant.options` to swap.
   */
  #findVariant(value, currentVariant, siblingValues) {
    if (!this.#productData || !this.#productData.variants) return null;

    // Preserve every other option dimension (e.g. subscription term) when one
    // option changes — swap out only the slot that actually holds a sibling
    // value instead of matching on the new value alone, which would always
    // resolve to the first variant carrying it regardless of what else is selected.
    if (currentVariant && currentVariant.options && siblingValues) {
      const optionIndex = currentVariant.options.findIndex((opt) => siblingValues.includes(opt));

      if (optionIndex !== -1) {
        const targetOptions = [...currentVariant.options];
        targetOptions[optionIndex] = value;

        const exactMatch = this.#productData.variants.find(
          (v) =>
            v.options &&
            v.options.length === targetOptions.length &&
            v.options.every((opt, i) => opt === targetOptions[i])
        );
        if (exactMatch) return exactMatch;
      }
    }

    return this.#productData.variants.find((v) => {
      if (!v.options) return false;
      return v.options.some((opt) => opt === value);
    });
  }

  #collapseAllPanels() {
    const panels = this.querySelectorAll('.awp_purchase-type__panel');
    panels.forEach((panel) => {
      panel.classList.remove('awp_purchase-type__panel--expanded');
    });

    const shippingNote = this.querySelector('.awp_purchase-type__shipping-note');
    if (shippingNote) {
      shippingNote.classList.remove('awp_purchase-type__shipping-note--visible');
    }
  }

  #expandPanelFor(type) {
    const panel = this.querySelector(`[data-panel="${type}"]`);
    if (panel) {
      panel.classList.add('awp_purchase-type__panel--expanded');
    }

    if (type === 'onetime') {
      const shippingNote = this.querySelector('.awp_purchase-type__shipping-note');
      if (shippingNote) {
        shippingNote.classList.add('awp_purchase-type__shipping-note--visible');
      }
    }
  }

  #updateSellingPlan(type) {
    if (!this.refs.sellingPlanInput) return;

    if (type === 'onetime') {
      this.refs.sellingPlanInput.value = '';
    } else if (type === 'usage') {
      if (this.#productData && this.#productData.sellingPlanGroups) {
        let group = this.#productData.sellingPlanGroups[0];
        for (const g of this.#productData.sellingPlanGroups) {
          if (g.app_id === '5859381') {
            group = g;
            break;
          }
        }
        if (group && group.selling_plans && group.selling_plans.length > 0) {
          this.refs.sellingPlanInput.value = group.selling_plans[0].id;
        }
      }
    } else if (type === 'frequency') {
      if (this.refs.frequencySelect) {
        this.refs.sellingPlanInput.value = this.refs.frequencySelect.value;
      }
    }
  }

  #resetButton(success) {
    const btn = this.refs.addToCartBtn;
    if (!btn) return;

    setTimeout(() => {
      btn.disabled = false;
      btn.classList.remove('awp_add-to-cart__btn--loading');
      btn.textContent = this.#currentAddText();

      if (!this.#productData || !this.#productData.available) {
        btn.disabled = true;
        btn.textContent = btn.dataset.soldText || 'Sold Out';
      }
    }, success ? 1000 : 500);
  }
}

if (!customElements.get('awp-product-form')) {
  customElements.define('awp-product-form', AwpProductForm);
}

/**
 * PDP async size switch.
 *
 * The size options (e.g. 12oz / 5lb) are separate products, so each is a plain
 * link to the other product's page. On the PDP we intercept that click, fetch
 * the target product, and swap the product content region in place instead of a
 * full-page navigation. Quick-add-modal size clicks are handled by
 * awp-quick-add.js, so those are ignored here.
 */
const AWP_PRODUCT_CONTENT_SELECTOR = '[data-product-grid-content]';

// Only swap the elements the quick-add modal already proves safe to re-render.
// Swapping the whole grid content also re-inserted blocks whose own click/connect
// behavior hijacked the next size click (redirecting to the homepage), so keep the
// blast radius small: the interactive form, the price, the media, and the title.
const AWP_SWAP_SELECTORS = ['awp-product-form', 'product-price', '.product-information__media', 'h1'];
let awpDidSizeSwap = false;

/** @param {string} url */
async function awpSwapProductContent(url) {
  const liveRoot = document.querySelector(AWP_PRODUCT_CONTENT_SELECTOR);
  if (!(liveRoot instanceof HTMLElement)) {
    window.location.href = url;
    return;
  }

  liveRoot.setAttribute('aria-busy', 'true');
  liveRoot.style.opacity = '0.5';
  liveRoot.style.pointerEvents = 'none';

  try {
    const response = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const freshRoot = doc.querySelector(AWP_PRODUCT_CONTENT_SELECTOR);
    if (!freshRoot) throw new Error('Product content not found in response');

    let swapped = false;
    for (const selector of AWP_SWAP_SELECTORS) {
      const liveEls = liveRoot.querySelectorAll(selector);
      const freshEls = freshRoot.querySelectorAll(selector);
      const count = Math.min(liveEls.length, freshEls.length);
      for (let i = 0; i < count; i++) {
        liveEls[i].replaceWith(document.importNode(freshEls[i], true));
        swapped = true;
      }
    }
    if (!swapped) throw new Error('No swappable product elements found');

    // The swapped-in grind selector's inline <script> doesn't re-run (cloned
    // scripts don't execute), so its dropdown needs an explicit re-init.
    if (typeof window.awpReinitGrindSelector === 'function') window.awpReinitGrindSelector();

    awpDidSizeSwap = true;
    window.history.pushState({ awpSizeSwap: true }, '', url);
    const newTitle = doc.querySelector('title')?.textContent;
    if (newTitle) document.title = newTitle;
  } catch (error) {
    // Fall back to a normal navigation so the user still gets the product.
    console.error('AWP size switch failed, navigating instead', error);
    window.location.href = url;
    return;
  } finally {
    liveRoot.removeAttribute('aria-busy');
    liveRoot.style.removeProperty('opacity');
    liveRoot.style.removeProperty('pointer-events');
  }
}

/** @param {MouseEvent} event */
function awpOnSizeSelectorClick(event) {
  // Let modified clicks (new tab, etc.) behave normally.
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = /** @type {HTMLElement | null} */ (event.target);
  const btn = target?.closest?.('.awp_size-selector__btn');
  if (!(btn instanceof HTMLAnchorElement)) return;

  // The quick-add modal handles its own size clicks (awp-quick-add.js).
  if (btn.closest('#awp-quick-add-dialog')) return;

  // Only intercept on a PDP (where the swappable product content exists).
  if (!document.querySelector(AWP_PRODUCT_CONTENT_SELECTOR)) return;

  // Own this click completely so no other handler (theme link nav, etc.) can act
  // on it — that competing navigation is what sent the user to the homepage.
  event.preventDefault();
  event.stopImmediatePropagation();

  if (btn.classList.contains('awp_size-selector__btn--active')) return;
  awpSwapProductContent(btn.href);
}

if (!window.__awpSizeSwitchBound) {
  window.__awpSizeSwitchBound = true;
  // Capture phase so we run before any competing click handler and can stop it.
  document.addEventListener('click', awpOnSizeSelectorClick, true);
  // A swapped product changes the URL via pushState. Once the user has swapped,
  // reload on back/forward so the whole page (URL, breadcrumbs, structured data)
  // matches the entry being navigated to. Users who never swapped keep normal
  // (bfcache-friendly) history behavior.
  window.addEventListener('popstate', () => {
    if (awpDidSizeSwap) window.location.reload();
  });
}
